import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { ENABLED_CODES, DEFAULT_LOCALE, NAMESPACES, normalizeLocale, getLocaleConfig as getLocaleConfigFn, intlLocaleFor as intlLocaleForFn } from "./locales";

export { SUPPORTED_LOCALES, ENABLED_LOCALES, getLocaleConfig, intlLocaleFor } from "./locales";
export const SUPPORTED_LANGS = ENABLED_CODES;
export const DEFAULT_LANG = DEFAULT_LOCALE;

// Language packs chargés automatiquement : i18n/<code>/<namespace>.json (aucune liste de langues ici).
const resources = {};
const packs = require.context("./", true, /^\.\/[a-z]{2}\/[A-Za-z]+\.json$/);
packs.keys().forEach((k) => {
  const [, code, ns] = k.match(/^\.\/([a-z]{2})\/([A-Za-z]+)\.json$/);
  if (!ENABLED_CODES.includes(code)) return;
  resources[code] = resources[code] || {};
  resources[code][ns] = packs(k);
});

// Priorité : préférence explicite en localStorage -> sinon langue du navigateur -> fallback FR.
// (La locale de l'utilisateur authentifié est appliquée par AuthContext via i18n.changeLanguage.)
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGS,
    nonExplicitSupportedLngs: true, // en-US -> en, fr-FR -> fr
    load: "languageOnly",
    ns: NAMESPACES,
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "securepass_lang",
      // Pas de mise en cache automatique : seule une préférence explicite (setLanguage) est mémorisée.
      caches: [],
    },
    returnEmptyString: false,
    saveMissing: process.env.NODE_ENV === "development",
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] clé manquante: ${lng}/${ns}/${key}`);
      }
    },
  });

export const normalizeLang = normalizeLocale;

// <html lang> suit la langue active (valeur issue du registre).
const syncHtmlLang = (l) => { if (typeof document !== "undefined") document.documentElement.lang = getLocaleConfigFn(l).htmlLang; };
i18n.on("languageChanged", syncHtmlLang);
syncHtmlLang(i18n.language);

// Change la langue de l'UI + mémorise la préférence (visiteur anonyme ou avant persistance serveur).
export const setLanguage = (lng) => {
  const l = normalizeLang(lng);
  i18n.changeLanguage(l);
  try { localStorage.setItem("securepass_lang", l); } catch (e) { /* noop */ }
  return l;
};

// Dates localisées : locale Intl issue du registre (jamais de locale Intl en dur dans les composants).
export const formatDateTime = (iso, opts) => (iso ? new Date(iso).toLocaleString(intlLocaleForFn(i18n.language), opts) : "—");
export const formatNumber = (n, opts) => (n == null ? "" : Number(n).toLocaleString(intlLocaleForFn(i18n.language), opts));

// Nom de pays localisé (ISO alpha-2) via Intl.DisplayNames — zéro table manuelle.
const _dnCache = {};
export const countryName = (code, lng) => {
  const l = normalizeLang(lng || i18n.language);
  if (!code) return "";
  try {
    _dnCache[l] = _dnCache[l] || new Intl.DisplayNames([intlLocaleForFn(l)], { type: "region" });
    return _dnCache[l].of(String(code).toUpperCase()) || code;
  } catch (e) {
    return code;
  }
};

// Formats localisés (Intl). La devise vient toujours du backend/pricing, jamais de la langue.
export const formatMoney = (minor, currency, decimals, lng) => {
  if (minor == null || !currency) return null;
  const d = decimals || 0;
  const v = minor / Math.pow(10, d);
  try {
    return new Intl.NumberFormat(intlLocaleForFn(lng || i18n.language), {
      style: "currency", currency, minimumFractionDigits: d, maximumFractionDigits: d,
    }).format(v);
  } catch (e) {
    return `${v} ${currency}`;
  }
};

export const formatDate = (iso, lng, opts) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(intlLocaleForFn(lng || i18n.language),
      opts || { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));
  } catch (e) {
    return iso;
  }
};

export default i18n;
